import { z } from "zod";
/**
 * The one definition of a valid catalog.
 *
 * This replaces `catalog.parser.ts`, which validated a pipe-separated text DSL.
 * Every rule the parser enforced lives here, minus the ones that were artifacts
 * of the text format (missing pipes, unknown section headers, line numbers) and
 * plus a few the parser was missing — see the notes on each.
 *
 * The list names match `AgentContextResponse` exactly, so the catalog the CRM
 * loads (`CatalogEntry.draft`) and the agent context read the same. The editor
 * saves one row at a time, so the per-row schemas below are what the endpoints
 * validate; `catalogPayloadSchema` is the shape of the whole catalog.
 * See docs/plans/catalog-forms-redesign.md.
 *
 * `programs` and `datedPrograms` used to be two lists whose ONLY difference was
 * the schedule type — which is why a tenant with no calendar (a machinery
 * dealer, a store) could not express a product at all. They are now one
 * `products` list carrying an optional `schedule`, matching the spine split in
 * docs/plans/multi-industry-capability-layer.md §8. The `catalogs` table the old
 * batch save wrote is history only; nothing reads it back.
 */
// Cycle keys are data, not a derived value, and the convention is deliberately
// non-obvious: the pilot's `2026-2` is labelled "Enero – Junio 2027" because
// cycle 2 of school year 2026 starts in January. Never "correct" a key from its
// label or vice versa.
const CYCLE_KEY_RE = /^\d{4}-\d+$/;
const CUSTOM_FIELD_NAME_RE = /^[a-z][a-z0-9_]{1,63}$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Lead columns a custom field would shadow. */
export const RESERVED_CUSTOM_FIELD_NAMES = [
    "name",
    "phone",
    "email",
    "product_interest",
    "score",
    "stage",
    "session_ref",
    "ai_paused",
];
const RESERVED_SET = new Set(RESERVED_CUSTOM_FIELD_NAMES);
/**
 * A real calendar date, not merely a well-shaped one.
 *
 * `new Date("2026-02-30")` does NOT return Invalid Date — it rolls over to
 * March 2, and the old parser accepted it, so an admin who typed Feb 30 silently
 * got a March 2 product. Month 13 and month 00 are rejected by `Date` itself;
 * day overflow is only caught by comparing the round-trip.
 */
function isRealIsoDate(s) {
    if (!ISO_DATE_RE.test(s))
        return false;
    const d = new Date(s);
    if (Number.isNaN(d.getTime()))
        return false;
    return d.toISOString().slice(0, 10) === s;
}
// Trimmed on the way in so " " never reaches a NOT NULL column and so a
// trailing space can't create a second "different" program.
const trimmed = (max, label) => z
    .string()
    .trim()
    .min(1, `${label} no puede estar vacío`)
    .max(max, `${label} no puede exceder ${max} caracteres`);
// ── Entities ─────────────────────────────────────────────────────────────────
/**
 * The live row this entry is — the address every per-row save uses, which is
 * what makes a rename an UPDATE of the same row (every lead keeps pointing at
 * it) rather than a new product.
 *
 * Optional because a row the admin just added has no id until it is created.
 */
const rowId = z.string().uuid("El identificador de la fila no es válido").optional();
const cycleFields = {
    // VarChar(20) in the DB — the regex already bounds it, but be explicit.
    key: z
        .string()
        .trim()
        .regex(CYCLE_KEY_RE, "La clave debe tener el formato AAAA-N, por ejemplo 2026-1")
        .max(20, "La clave no puede exceder 20 caracteres"),
    // The parser never validated this, so an empty or over-long label reached
    // `cycles.label` (VarChar(100), NOT NULL) and surfaced as a 500.
    label: trimmed(100, "La etiqueta del ciclo"),
};
/**
 * One optional group of products — what they ARE (Primaria, Destilados,
 * Postres). Untyped: each product keeps its own calendar, so one category can
 * hold both a program by cycle and one with its own dates.
 */
export const catalogCategorySchema = z.object({
    /** @see rowId */
    id: rowId,
    name: trimmed(100, "El nombre de la categoría"),
});
/** A category row, or null for the catalog's root. */
const categoryIdSchema = z.string().uuid("La categoría no es válida").nullable();
export const catalogCycleSchema = z.object({
    /** @see rowId */
    id: rowId,
    ...cycleFields,
});
/**
 * How a product is sold against a calendar. `null` on the product means it is
 * not — which is the normal case outside education, and was unrepresentable
 * before the spine split.
 *
 * `cycles` carries no dates of its own: the product is offered in whichever
 * intake periods are open, and the dates live on the cycle. Rejecting dates
 * there keeps the two ways of expressing a schedule from overlapping.
 */
export const catalogScheduleSchema = z
    .object({
    type: z.enum(["cycles", "specific_dates"], {
        errorMap: () => ({ message: 'El tipo de calendario debe ser "cycles" o "specific_dates"' }),
    }),
    /** Both null = offered without a confirmed schedule. */
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
})
    .superRefine((sch, ctx) => {
    const hasStart = sch.startDate !== null;
    const hasEnd = sch.endDate !== null;
    if (sch.type === "cycles") {
        if (hasStart || hasEnd) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Un producto que se ofrece por periodo no lleva fechas propias — las fechas las define el periodo",
                path: [hasStart ? "startDate" : "endDate"],
            });
        }
        return;
    }
    // A half-open range is ambiguous: reject rather than guess.
    if (hasStart !== hasEnd) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Define ambas fechas o deja las dos vacías si aún no hay fecha definida",
            path: [hasStart ? "endDate" : "startDate"],
        });
        return;
    }
    if (!hasStart || !hasEnd)
        return;
    let valid = true;
    if (!isRealIsoDate(sch.startDate)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "La fecha de inicio no es una fecha válida (AAAA-MM-DD)",
            path: ["startDate"],
        });
        valid = false;
    }
    if (!isRealIsoDate(sch.endDate)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "La fecha de fin no es una fecha válida (AAAA-MM-DD)",
            path: ["endDate"],
        });
        valid = false;
    }
    // ISO date strings compare correctly as strings.
    if (valid && sch.startDate > sch.endDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "La fecha de fin debe ser igual o posterior a la de inicio",
            path: ["endDate"],
        });
    }
});
/**
 * One recited spec — "Potencia: 180 HP", "Cosecha: 2018".
 *
 * These are for things the system never queries; the agent just says them. The
 * caps are not cosmetic: this text is interpolated into the CACHED prompt
 * prefix, so it is prompt input and gets prompt-input bounds (CLAUDE.md §6.12),
 * the same way the contact and follow-up tool outputs are capped.
 */
export const catalogAttributeSchema = z.object({
    key: trimmed(40, "La etiqueta del atributo"),
    value: trimmed(120, "El valor del atributo"),
});
/** Beyond this a catalog line stops being a spec sheet and starts being a KB. */
export const MAX_PRODUCT_ATTRIBUTES = 20;
/**
 * What an admin may write about one product. Below the `VarChar(500)` column on
 * purpose: the text lands in the CACHED prompt prefix on every turn, once per
 * product, so it is sized as one or two sentences that introduce the product —
 * longer detail belongs in the knowledge base. Shared so the CRM counter, the
 * dialog and the route enforce one number.
 */
export const MAX_PRODUCT_DESCRIPTION_LENGTH = 300;
/** Empty text is "no description", stored as null — never as "". */
const productDescriptionSchema = z
    .string()
    .trim()
    .max(MAX_PRODUCT_DESCRIPTION_LENGTH, `La descripción no puede exceder ${MAX_PRODUCT_DESCRIPTION_LENGTH} caracteres`)
    .nullable()
    .transform((v) => (v === "" ? null : v));
/**
 * Integer minor units (centavos), never a float.
 *
 * The precedent is `clients.currentPriceAmountCents`, and the reason is the
 * usual one: 19.90 is not representable in binary floating point, and a
 * catalog price is summed into an order total that a real person pays in
 * cash. Null means "no price on file" — the agent quotes nothing and the
 * cart refuses the line rather than inventing a number.
 */
const priceCentsSchema = z
    .number()
    .int("El precio debe ser un número entero de centavos")
    .min(0, "El precio no puede ser negativo")
    .nullable();
/** One catalog row, in any industry. `products.name` is VarChar(255). */
export const catalogProductSchema = z
    .object({
    /** @see rowId */
    id: rowId,
    name: trimmed(255, "El nombre del producto"),
    /** Null = the agent says only the name. See `MAX_PRODUCT_DESCRIPTION_LENGTH`. */
    description: productDescriptionSchema,
    attributes: z
        .array(catalogAttributeSchema)
        .max(MAX_PRODUCT_ATTRIBUTES, `No puedes definir más de ${MAX_PRODUCT_ATTRIBUTES} atributos por producto`),
    /** Null = sold without a calendar. */
    schedule: catalogScheduleSchema.nullable(),
    /** Null = the catalog's root. */
    categoryId: categoryIdSchema.default(null),
    /** See `priceCentsSchema`. */
    priceCents: priceCentsSchema
        // Defaulted so a catalog written by hand (seeds, fixtures) need not say it.
        .default(null),
})
    .superRefine((p, ctx) => {
    // Two attributes under one label render as a contradiction in the prompt —
    // the agent reads both and states whichever it saw last.
    flagDuplicates(p.attributes.map((a) => a.key), "attributes", "key", (v) => `El atributo "${v}" ya está definido`, ctx);
});
const customFieldFields = {
    name: z
        .string()
        .trim()
        .regex(CUSTOM_FIELD_NAME_RE, "Debe empezar con minúscula y contener solo a-z, 0-9 y _ (2 a 64 caracteres)"),
    // Unvalidated by the parser; `custom_field_definitions.label` is
    // VarChar(255) NOT NULL.
    label: trimmed(255, "La etiqueta del campo"),
    fieldType: z.enum(["select", "text"], {
        errorMap: () => ({ message: 'El tipo debe ser "select" o "text"' }),
    }),
    /** Non-null only for `select`. */
    options: z.array(z.string().trim()).nullable(),
};
/** The rules that span a custom field's own fields — shared by every shape of it. */
function refineCustomField(f, ctx) {
    if (RESERVED_SET.has(f.name)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `"${f.name}" es un nombre reservado del sistema`,
            path: ["name"],
        });
    }
    if (f.fieldType === "select") {
        const nonEmpty = (f.options ?? []).filter((o) => o.length > 0);
        if (nonEmpty.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Un campo de tipo select necesita al menos una opción",
                path: ["options"],
            });
        }
        if (new Set(nonEmpty).size !== nonEmpty.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Las opciones no pueden repetirse",
                path: ["options"],
            });
        }
        return;
    }
    // A `text` field carrying options is a half-finished type switch. Rejecting
    // it keeps the stored shape unambiguous.
    if (f.options !== null && f.options.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Un campo de tipo texto no lleva opciones",
            path: ["options"],
        });
    }
}
export const catalogCustomFieldSchema = z
    .object({
    /** @see rowId */
    id: rowId,
    ...customFieldFields,
})
    .superRefine(refineCustomField);
/** ISO-4217 code every priced row is quoted in. */
const currencyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(3, "El código de moneda debe tener 3 letras (por ejemplo MXN)");
// ── Instant save ─────────────────────────────────────────────────────────────
//
// The catalog editor saves each edit the moment it is made — one row, one
// field — instead of one reviewed batch the admin could forget to press. These
// are the bodies of those per-row endpoints.
//
// An update carries only the fields that changed. The server merges it over the
// stored row and validates the RESULT against the full row schema above, so a
// rule that spans two fields (a select needs options, a range needs both dates)
// holds no matter which of them one request touched.
const NOTHING_TO_SAVE = "No hay cambios que guardar";
const hasAnyField = (value) => Object.values(value).some((v) => v !== undefined);
export const createCatalogProductSchema = z.object({
    name: trimmed(255, "El nombre del producto"),
    /** Chosen when the row is added: none, by cycle, or with its own dates. */
    schedule: catalogScheduleSchema.nullable().default(null),
    priceCents: priceCentsSchema.default(null),
    /** The group it was added in; null is the root. */
    categoryId: categoryIdSchema.default(null),
});
export const updateCatalogProductSchema = z
    .object({
    name: trimmed(255, "El nombre del producto").optional(),
    description: productDescriptionSchema.optional(),
    schedule: catalogScheduleSchema.nullable().optional(),
    priceCents: priceCentsSchema.optional(),
    categoryId: categoryIdSchema.optional(),
})
    .refine(hasAnyField, NOTHING_TO_SAVE);
export const createCatalogCategorySchema = catalogCategorySchema.omit({ id: true });
export const updateCatalogCategorySchema = createCatalogCategorySchema;
export const createCatalogCycleSchema = z.object(cycleFields);
export const updateCatalogCycleSchema = z
    .object(cycleFields)
    .partial()
    .refine(hasAnyField, NOTHING_TO_SAVE);
export const createCatalogCustomFieldSchema = z
    .object(customFieldFields)
    .superRefine(refineCustomField);
/** Partial on purpose; the merged row is re-validated with `createCatalogCustomFieldSchema`. */
export const updateCatalogCustomFieldSchema = z
    .object(customFieldFields)
    .partial()
    .refine(hasAnyField, NOTHING_TO_SAVE);
/**
 * The two catalog-wide values. `usesCycles: false` deactivates every active
 * cycle in the same transaction — open enrollment and a list of cycles are
 * contradictory, the rule `catalogPayloadSchema` states for the batch shape.
 */
export const updateCatalogSettingsSchema = z
    .object({
    currency: currencyCodeSchema.optional(),
    usesCycles: z.boolean().optional(),
})
    .refine(hasAnyField, NOTHING_TO_SAVE);
// ── Cross-list rules ─────────────────────────────────────────────────────────
/**
 * Reports every duplicate after the first, so each offending row gets a message.
 *
 * Blank values are skipped: they already carry their own "cannot be empty"
 * error, and calling two blanks "duplicates" would put a second, more confusing
 * message on the same field.
 */
function flagDuplicates(values, listName, field, message, ctx) {
    const seen = new Set();
    values.forEach((value, i) => {
        if (value === "")
            return;
        if (seen.has(value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: message(value),
                path: [listName, i, field],
            });
            return;
        }
        seen.add(value);
    });
}
export const catalogPayloadSchema = z
    .object({
    /**
     * Does this institution run on school cycles?
     *
     * `false` is the academy case — dance classes, evergreen enrollment: the
     * prospect can start whenever. This is stated rather than inferred from an
     * empty `cycles` list, because an academy and a school that simply let its
     * cycles lapse look identical in the data. Inferring would silently flip
     * that school from "consultar disponibilidad" to promising immediate
     * enrollment — a quiet failure in the over-promising direction.
     *
     * It lives in the catalog rather than in client settings because it governs
     * how the catalog's own rows are read.
     */
    usesCycles: z.boolean(),
    /**
     * ISO-4217 code every priced row is quoted in.
     *
     * One value for the whole catalog rather than one per row, even though
     * `products.currency` is a per-row column: an order carries ONE currency,
     * and a cart that mixes two is refused at add time. Making mixed currencies
     * unrepresentable here is cheaper than detecting them later, and no tenant
     * has asked for two.
     */
    currency: currencyCodeSchema
        // The same default as `clients.catalog_currency`.
        .default("MXN"),
    /** Active categories in order. Defaulted so a catalog written by hand need not list any. */
    categories: z.array(catalogCategorySchema).default([]),
    cycles: z.array(catalogCycleSchema),
    products: z.array(catalogProductSchema),
    customFields: z.array(catalogCustomFieldSchema),
})
    .superRefine((cat, ctx) => {
    // Open enrollment and a list of cycles are contradictory; the server refuses a
    // cycle under open enrollment and deactivates them all on the switch.
    if (!cat.usesCycles && cat.cycles.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Un catálogo de inscripción abierta no puede tener ciclos configurados",
            path: ["cycles"],
        });
    }
    flagDuplicates(cat.categories.map((c) => c.name), "categories", "name", (v) => `La categoría "${v}" ya está definida`, ctx);
    flagDuplicates(cat.cycles.map((c) => c.key), "cycles", "key", (v) => `El ciclo "${v}" ya está definido`, ctx);
    flagDuplicates(cat.products.map((p) => p.name), "products", "name", (v) => `El producto "${v}" ya está definido`, ctx);
    flagDuplicates(cat.customFields.map((f) => f.name), "customFields", "name", (v) => `El campo "${v}" ya está definido`, ctx);
    // Two entries claiming the same live row would be two editor rows saving
    // over one record, the last one silently winning.
    flagDuplicates(cat.cycles.map((c) => c.id ?? ""), "cycles", "id", (v) => `Dos ciclos apuntan al mismo registro (${v})`, ctx);
    flagDuplicates(cat.customFields.map((f) => f.id ?? ""), "customFields", "id", (v) => `Dos campos apuntan al mismo registro (${v})`, ctx);
    flagDuplicates(cat.products.map((p) => p.id ?? ""), "products", "id", (v) => `Dos productos apuntan al mismo registro (${v})`, ctx);
    // The rule that used to live here — "this name already exists in the other
    // program list" — is gone with the second list. One name per client is now
    // enforced by the DB itself (`@@unique([clientId, name])`), which is what it
    // should always have been: two same-named rows reached the agent as two
    // identical `capture_qualification` enum entries it could not tell apart.
    //
    // Deliberately NOT added: "a product cannot be cycle-scheduled when
    // usesCycles is false". 31 live production rows are exactly that (the two
    // open-enrollment tenants), and the rule would flag catalogs that work.
    // Open enrollment is expressed by `usesCycles`, not by the rows.
});
/** An empty but valid catalog — the onboarding wizard's starting point. */
export const EMPTY_CATALOG = {
    usesCycles: true,
    currency: "MXN",
    categories: [],
    cycles: [],
    products: [],
    customFields: [],
};
/** Flattens a ZodError into the field errors the form renders. */
export function toCatalogFieldErrors(error) {
    return error.issues.map((issue) => ({
        path: [...issue.path],
        message: issue.message,
    }));
}
/**
 * Reads `products.attributes` back out of Postgres.
 *
 * The column is `jsonb` and Prisma types it as `Json`, i.e. `unknown` in
 * practice — nothing stops a hand-written row from holding a string or an
 * object. Rather than cast, this validates and drops what does not fit, so a
 * malformed row degrades to "no attributes" instead of interpolating `undefined`
 * into the agent's prompt. Rows written through `catalogPayloadSchema` always
 * round-trip whole.
 */
export function parseProductAttributes(value) {
    const parsed = z.array(catalogAttributeSchema).safeParse(value);
    return parsed.success ? parsed.data : [];
}
/**
 * Why a product cannot be deleted, in the words the admin reads — the first
 * reason only, in the order a school cares about. Shared so the CRM row and the
 * server's 409 can never phrase it differently.
 */
export function productUsageReason(usage) {
    const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
    if (usage.leads > 0)
        return `No se puede eliminar: tiene ${plural(usage.leads, "prospecto asociado", "prospectos asociados")}.`;
    if (usage.orders > 0)
        return `No se puede eliminar: aparece en ${plural(usage.orders, "pedido", "pedidos")}.`;
    if (usage.quotes > 0)
        return `No se puede eliminar: aparece en ${plural(usage.quotes, "solicitud de cotización", "solicitudes de cotización")}.`;
    if (usage.promotions > 0)
        return `No se puede eliminar: está en ${plural(usage.promotions, "promoción", "promociones")}.`;
    if (usage.documents > 0)
        return `No se puede eliminar: tiene ${plural(usage.documents, "documento ligado", "documentos ligados")}.`;
    return `No se puede eliminar: tiene ${plural(usage.stockMovements, "movimiento de inventario", "movimientos de inventario")}.`;
}
//# sourceMappingURL=catalog.js.map